import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import cn from 'classnames';
import { InputControlled, Button } from 'components';
import { useAuth } from 'contexts/AuthContext';

interface IFormInputs {
    email: string
    password: string
}


export const LoginForm = () => {

    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { signIn, emailVerificationRequired, emailVerificationData } = useAuth();
    const [fadeIn, setFadeIn] = useState(false);

    const schema = yup.object({
        email: yup.string().email(t('error.invalidEmail')).required(t('error.requiredEmail')),
        password: yup.string().required(t('error.requiredPassword')).min(6, t('error.invalidPassword')),
    }).required();

    const { handleSubmit, control, reset, formState: { errors } } = useForm<IFormInputs>({
        resolver: yupResolver(schema)
    });

    const handleOnSubmit: SubmitHandler<IFormInputs> = async data => {
        const { email, password } = data;
        try {
            setLoading(true);
            const result = await signIn(email, password);

            // Si signIn retorna sin resultado, significa que hay verificación de email pendiente
            // La navegación se manejará en el useEffect de arriba
            if (result) {
                // Login exitoso directo, navegar al dashboard
                navigate('/');
            }
        } catch (error: any) {
            console.log({ error });
            toast.error(t('errorLogin'));
        } finally {
            reset();
            setLoading(false);
        }
    }

    useEffect(() => {
        setTimeout(() => {
            setFadeIn(true);
        }, 500);
    }, [])

    // Navegación automática a verificación de email
    useEffect(() => {
        if (emailVerificationRequired && emailVerificationData) {
            console.log('Redirecting to email verification');
            navigate('/auth/email-verification', { replace: true });
        }
    }, [emailVerificationRequired, emailVerificationData, navigate]);

    return (
        <div>
            <h2 className={cn("mb-8 text-3xl c-orange font-light enter-y delay-100", {fadeIn})}>{t('login')}</h2>
            <form onSubmit={handleSubmit(handleOnSubmit)}>
                 <InputControlled
                    name="email"
                    control={control}
                    className={cn("block mb-5 enter-y delay-200", {fadeIn})}
                    labelClass="sm"
                    label={t('email')}
                    placeholder={t('enterYourEmailAddress')}
                />
                <InputControlled
                    name="password"
                    control={control}
                    type="password"
                    className={cn("block mb-5 enter-y delay-300", {fadeIn})}
                    labelClass="sm"
                    label={t('password')}
                    placeholder={t('enterYourPassword')}
                />
                <div className={cn("flex justify-between mt-8 enter-y delay-500", {fadeIn})}>
                    <Button type="submit" loading={loading} className="c-orange font-extrabold text-sm">+ {t('signIn')}</Button>
                    <Button type="link" to="/auth/forgot-password" underline className="c-orange text-sm">{t('forgotPassword')}</Button>
                </div>
            </form>
        </div>
    )
}